/**
 * Local Print Service Integration
 * 
 * This utility provides functions to print receipts via the Local Print Service
 * (a Java desktop application running on localhost:3001).
 * 
 * Note: Uses port 3001 to avoid conflict with React dev server (port 3000).
 * 
 * Uses Plain Text printing for best compatibility with receipt printers.
 * 
 * If the service is not available, it will throw an error that can be caught
 * to fall back to browser print dialog.
 */

const PRINT_SERVICE_URL = 'http://localhost:3001/print'
const RECEIPT_WIDTH = 40 // Characters wide (standard receipt width)

/**
 * Converts receipt data to styled HTML format
 */
const formatReceiptAsHTML = (receiptData) => {
  const {
    companyName = '',
    companyAddress = '',
    companyPhone = '',
    branchName = 'Branch',
    cashierName = 'Cashier',
    receiptNumber = 'DM-0000',
    customerName = 'Walk-in customer',
    cartItems = [],
    subtotal = 0,
    discount = 0,
    discountName = null,
    total = 0,
    paymentMethod = 'cash',
    amountReceived = 0,
    changeDue = 0,
    isZigPayment = false,
    paymentMethodFormatted = null,
  } = receiptData

  const currentDate = new Date().toLocaleString()
  const isZig = isZigPayment || (paymentMethod && paymentMethod.toLowerCase().includes('zig'))
  const currencySymbol = isZig ? 'ZIG' : 'USD'
  const paymentDisplay = paymentMethodFormatted || paymentMethod?.toUpperCase() || ''

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      html, body {
        width: 100%;
        height: 100%;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 11px; /* Slightly smaller font for better fit */
        line-height: 1.3;
        color: #000000;
        background: #ffffff;
      }
      body {
        padding: 15px;
        display: block;
        visibility: visible;
      }
      .receipt-container {
        width: 100%;
        max-width: 300px;
        margin: 0 auto;
        display: block;
      }
      .receipt-header {
        text-align: center;
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 2px solid #000000;
        display: block;
      }
      .receipt-header h1 {
        font-size: 18px;
        font-weight: bold;
        margin-bottom: 5px;
        color: #2ca01c;
        display: block;
      }
      .receipt-header p {
        font-size: 10px;
        margin: 2px 0;
        color: #333333;
        display: block;
      }
      .receipt-info {
        margin: 10px 0;
        font-size: 11px;
        display: block;
      }
      .receipt-info-row {
        margin: 4px 0;
        display: block;
        width: 100%;
        overflow: hidden;
      }
      .receipt-info-label {
        font-weight: bold;
        float: left;
        display: inline-block;
      }
      .receipt-info-value {
        text-align: right;
        float: right;
        display: inline-block;
      }
      .divider {
        border-top: 1px dashed #000000;
        margin: 10px 0;
        display: block;
        clear: both;
      }
      .section-title {
        font-weight: bold;
        font-size: 12px;
        margin: 10px 0 5px 0;
        display: block;
      }
      .items-list {
        margin: 10px 0;
        display: block;
      }
      .item-row {
        margin: 5px 0;
        font-size: 11px;
        display: block;
        width: 100%;
        overflow: hidden;
      }
      .item-name {
        float: left;
        display: inline-block;
        margin-right: 10px;
      }
      .item-price {
        font-weight: bold;
        text-align: right;
        float: right;
        display: inline-block;
        white-space: nowrap;
      }
      .totals {
        margin: 15px 0;
        display: block;
      }
      .total-row {
        margin: 6px 0;
        font-size: 11px;
        display: block;
        width: 100%;
        overflow: hidden;
        clear: both;
      }
      .total-label {
        font-weight: bold;
        float: left;
        display: inline-block;
      }
      .total-value {
        font-weight: bold;
        text-align: right;
        float: right;
        display: inline-block;
      }
      .total-final {
        font-size: 14px;
        border-top: 2px solid #000000;
        padding-top: 8px;
        margin-top: 8px;
      }
      .discount-row {
        color: #2ca01c;
      }
      .change-row {
        color: #2ca01c;
        font-size: 12px;
      }
      .footer {
        text-align: center;
        margin-top: 20px;
        padding-top: 10px;
        border-top: 1px dashed #000000;
        font-size: 10px;
        color: #666666;
        display: block;
        clear: both;
      }
      .footer p {
        display: block;
        margin: 2px 0;
      }
      @media print {
        body {
          padding: 10px;
        }
      }
    </style>
  </head>
  <body>
    <div class="receipt-container">
      <div class="receipt-header">
        <h1>${companyName}</h1>
        ${companyAddress ? `<p>${companyAddress}</p>` : ''}
        ${companyPhone ? `<p>Phone: ${companyPhone}</p>` : ''}
      </div>

      <div class="receipt-info">
        <div class="receipt-info-row">
          <span class="receipt-info-label">Branch:</span>
          <span class="receipt-info-value">${branchName}</span>
        </div>
        <div class="receipt-info-row">
          <span class="receipt-info-label">Cashier:</span>
          <span class="receipt-info-value">${cashierName}</span>
        </div>
        <div class="receipt-info-row">
          <span class="receipt-info-label">Receipt No:</span>
          <span class="receipt-info-value">${receiptNumber}</span>
        </div>
        <div class="receipt-info-row">
          <span class="receipt-info-label">Date:</span>
          <span class="receipt-info-value">${currentDate}</span>
        </div>
      </div>

      <div class="divider"></div>

      <div class="section-title">Customer: ${customerName}</div>

      <div class="divider"></div>

      <div class="section-title">Items</div>
      <div class="items-list">
        ${cartItems.map(item => {
          const itemTotal = (item.price * item.quantity).toFixed(2)
          return `
        <div class="item-row">
          <span class="item-name">${item.name} x${item.quantity}</span>
          <span class="item-price">${currencySymbol} ${itemTotal}</span>
        </div>
        `
        }).join('')}
      </div>

      <div class="divider"></div>

      <div class="totals">
        <div class="total-row">
          <span class="total-label">Subtotal</span>
          <span class="total-value">${currencySymbol} ${subtotal.toFixed(2)}</span>
        </div>
        ${discount > 0 && discountName ? `
        <div class="total-row discount-row">
          <span class="total-label">Discount (${discountName})</span>
          <span class="total-value">-${currencySymbol} ${discount.toFixed(2)}</span>
        </div>
        ` : ''}
        <div class="total-row total-final">
          <span class="total-label">Total</span>
          <span class="total-value">${currencySymbol} ${total.toFixed(2)}</span>
        </div>
        <div class="total-row">
          <span class="total-label">Payment Method</span>
          <span class="total-value">${paymentDisplay}</span>
        </div>
        <div class="total-row">
          <span class="total-label">Amount Received</span>
          <span class="total-value">${currencySymbol} ${parseFloat(amountReceived).toFixed(2)}</span>
        </div>
        <div class="total-row change-row">
          <span class="total-label">Change</span>
          <span class="total-value">${currencySymbol} ${changeDue.toFixed(2)}</span>
        </div>
      </div>

      <div class="divider"></div>

      <div class="footer">
        <p>Thank you for choosing</p>
        <p><strong>${companyName || 'our store'}!</strong></p>
      </div>
    </div>
  </body>
</html>`
}

/**
 * Converts receipt data to plain text format (ASCII only for compatibility)
 */
const formatReceiptAsText = (receiptData) => {
  const {
    companyName = '',
    companyAddress = '',
    companyPhone = '',
    branchName = 'Branch',
    cashierName = 'Cashier',
    receiptNumber = 'DM-0000',
    customerName = 'Walk-in customer',
    cartItems = [],
    subtotal = 0,
    total = 0,
    paymentMethod = 'cash',
    amountReceived = 0,
    changeDue = 0,
    isZigPayment = false,
    paymentMethodFormatted = null,
  } = receiptData

  const isZig = isZigPayment || (paymentMethod && paymentMethod.toLowerCase().includes('zig'))
  const currencySymbol = isZig ? 'ZIG' : 'USD'
  const paymentDisplay = paymentMethodFormatted || paymentMethod?.toUpperCase() || ''

  // Use simple ASCII characters only (no Unicode)
  const separator = '--------------------------------'
  
  const lines = [
  '',
  `         ${companyName || ''}`,
    companyAddress || '',
    companyPhone ? `Phone: ${companyPhone}` : '',
    separator,
    '',
    `Branch:     ${branchName}`,
    `Cashier:    ${cashierName}`,
    `Receipt No: ${receiptNumber}`,
    `Date:       ${new Date().toLocaleString()}`,
    '',
    `Customer: ${customerName}`,
    separator,
    '',
    'Items',
    '',
  ]

  // Add items (respect 40 characters per line for 57mm paper)
  cartItems.forEach((item) => {
    const itemTotal = (item.price * item.quantity).toFixed(2)
    const itemLine = `${item.name} x${item.quantity}`
    const priceLine = `${currencySymbol} ${itemTotal}`
    // Pad to align prices (max 40 chars per line for this printer)
    const maxWidth = RECEIPT_WIDTH
    const padding = Math.max(1, maxWidth - itemLine.length - priceLine.length)
    lines.push(`${itemLine}${' '.repeat(padding)}${priceLine}`)
  })

  lines.push('')
  lines.push(separator)
  lines.push('')
  lines.push(`Subtotal:${' '.repeat(24)}${currencySymbol} ${subtotal.toFixed(2)}`)
  lines.push(`Total:${' '.repeat(27)}${currencySymbol} ${total.toFixed(2)}`)
  lines.push('')
  lines.push(`Payment Method${' '.repeat(18)}${paymentDisplay}`)
  lines.push(`Amount Received${' '.repeat(19)}${currencySymbol} ${parseFloat(amountReceived).toFixed(2)}`)
  lines.push(`Change${' '.repeat(26)}${currencySymbol} ${changeDue.toFixed(2)}`)
  lines.push('')
  lines.push(separator)
  lines.push('')
  lines.push('  Thank you for choosing')
  lines.push(`     ${companyName || 'our store'}!`)
  lines.push('')
  lines.push('')

  return lines.join('\n')
}

/**
 * Converts receipt data to ESC/POS format (raw bytes)
 */
const formatReceiptAsEscPos = (receiptData) => {
  const {
    companyName = '',
    companyAddress = '',
    companyPhone = '',
    branchName = 'Branch',
    cashierName = 'Cashier',
    receiptNumber = 'DM-0000',
    customerName = 'Walk-in customer',
    cartItems = [],
    subtotal = 0,
    discount = 0,
    discountName = null,
    total = 0,
    paymentMethod = 'cash',
    amountReceived = 0,
    changeDue = 0,
    isZigPayment = false,
    paymentMethodFormatted = null,
  } = receiptData

  const isZig = isZigPayment || (paymentMethod && paymentMethod.toLowerCase().includes('zig'))
  const currencySymbol = isZig ? 'ZIG' : 'USD'
  const paymentDisplay = paymentMethodFormatted || paymentMethod?.toUpperCase() || ''

  const ESC = 0x1b
  const GS = 0x1d
  const encoder = new TextEncoder()
  const bytes = []

  // Initialize printer
  bytes.push(ESC, 0x40) // ESC @ - Initialize printer

  // Center align for header
  bytes.push(ESC, 0x61, 0x01) // ESC a 1 - Center alignment
  bytes.push(...encoder.encode(`${companyName}\n`))
  if (companyAddress) {
    bytes.push(...encoder.encode(`${companyAddress}\n`))
  }
  if (companyPhone) {
    bytes.push(...encoder.encode(`Phone: ${companyPhone}\n`))
  }

  // Left align for rest
  bytes.push(ESC, 0x61, 0x00) // ESC a 0 - Left alignment
  bytes.push(...encoder.encode('--------------------------------\n'))
  bytes.push(...encoder.encode(`Branch:     ${branchName}\n`))
  bytes.push(...encoder.encode(`Cashier:    ${cashierName}\n`))
  bytes.push(...encoder.encode(`Receipt No: ${receiptNumber}\n`))
  bytes.push(...encoder.encode(`Date:       ${new Date().toLocaleString()}\n`))
  bytes.push(...encoder.encode('\n'))
  bytes.push(...encoder.encode(`Customer: ${customerName}\n`))
  bytes.push(...encoder.encode('--------------------------------\n'))
  bytes.push(...encoder.encode('\nItems\n\n'))

  // Add items (respect 40 characters per line for 57mm paper)
  cartItems.forEach((item) => {
    const itemTotal = (item.price * item.quantity).toFixed(2)
    const itemLine = `${item.name} x${item.quantity}`
    const priceLine = `${currencySymbol} ${itemTotal}`
    const maxWidth = RECEIPT_WIDTH
    const padding = Math.max(1, maxWidth - itemLine.length - priceLine.length)
    bytes.push(...encoder.encode(`${itemLine}${' '.repeat(padding)}${priceLine}\n`))
  })

  bytes.push(...encoder.encode('\n--------------------------------\n'))
  bytes.push(...encoder.encode(`Subtotal:${' '.repeat(24)}${currencySymbol} ${subtotal.toFixed(2)}\n`))
  if (discount > 0 && discountName) {
    bytes.push(...encoder.encode(`Discount (${discountName})${' '.repeat(Math.max(1, 25 - discountName.length - 12))}-${currencySymbol} ${discount.toFixed(2)}\n`))
  }
  bytes.push(...encoder.encode(`Total:${' '.repeat(27)}${currencySymbol} ${total.toFixed(2)}\n`))
  bytes.push(...encoder.encode('\n'))
  bytes.push(...encoder.encode(`Payment Method${' '.repeat(18)}${paymentDisplay}\n`))
  bytes.push(...encoder.encode(`Amount Received${' '.repeat(19)}${currencySymbol} ${parseFloat(amountReceived).toFixed(2)}\n`))
  bytes.push(...encoder.encode(`Change${' '.repeat(26)}${currencySymbol} ${changeDue.toFixed(2)}\n`))
  bytes.push(...encoder.encode('\n--------------------------------\n'))
  bytes.push(...encoder.encode('\n  Thank you for choosing\n'))
  bytes.push(...encoder.encode(`    ${companyName}!\n`))
  bytes.push(...encoder.encode('\n\n'))

  // Feed paper and cut (partial cut)
  bytes.push(GS, 0x56, 0x41, 0x10) // GS V A 10 - Partial cut

  return new Uint8Array(bytes)
}

/**
 * Formats receipt data as plain text with proper alignment
 * @param {Object} receiptData - Receipt data object
 * @returns {string} - Formatted plain text receipt
 */
const formatReceiptAsPlainText = (receiptData) => {
  const {
    branchName = 'Main Branch',
    branchAddress = '',
    cashierName = 'Cashier',
    receiptNumber = 'DM-0000',
    customerName = 'Walk-in customer',
    cartItems = [],
    subtotal = 0,
    discount = 0,
    discountName = null,
    total = 0,
    paymentMethod = 'cash',
    paymentMethodFormatted = null,
    amountReceived = 0,
    changeDue = 0,
    isZigPayment = false,
    exchangeRate = null,
    usdEquivalent = null,
    companyName = '',
    companyAddress = '',
    companyPhone = '',
  } = receiptData

  const now = new Date()
  const date = now.toISOString().split('T')[0] // YYYY-MM-DD
  const time = now.toTimeString().split(' ')[0] // HH:MM:SS
  const timeFormatted = time.substring(0, 5) // HH:MM

  const separator = '='.repeat(RECEIPT_WIDTH)
  const lineSeparator = '-'.repeat(RECEIPT_WIDTH)

  // Helper function to center text
  const center = (text) => {
    const padding = Math.max(0, Math.floor((RECEIPT_WIDTH - text.length) / 2))
    return ' '.repeat(padding) + text
  }

  // Helper function to pad right
  const padRight = (text, width) => {
    return (text || '').substring(0, width).padEnd(width, ' ')
  }

  // Helper function to pad left
  const padLeft = (text, width) => {
    return (text || '').substring(0, width).padStart(width, ' ')
  }

  // Helper function to format currency
  const currency = (amount) => {
    return parseFloat(amount || 0).toFixed(2)
  }

  // Ensure any line does not exceed the printer's max width
  const clampLine = (text = '') => {
    const safe = text == null ? '' : String(text)
    return safe.length > RECEIPT_WIDTH ? safe.substring(0, RECEIPT_WIDTH) : safe
  }

  const lines = []
  
  // Header - company name (simulated "bold" by uppercase) and details
  lines.push(separator)
  lines.push('')
  if (companyName) {
    lines.push(clampLine(center(companyName.toUpperCase())))
    lines.push('')
  }
  if (companyAddress) {
    lines.push(clampLine(center(companyAddress)))
  }
  if (companyPhone) {
    lines.push(clampLine(center(`Phone: ${companyPhone}`)))
  }
  lines.push('')
  lines.push(clampLine(center('SALES RECEIPT')))
  lines.push('')
  lines.push(separator)
  lines.push('')
  
  // Branch and Cashier info (spaced slightly below header)
  lines.push(clampLine(`Branch:  ${branchName}`))
  lines.push(clampLine(`Cashier: ${cashierName}`))
  lines.push('')
  
  // Date and Time on one line
  lines.push(clampLine(`Date: ${date}     Time: ${timeFormatted}`))
  lines.push('')
  
  // Receipt Number
  lines.push(clampLine(`Receipt No: ${receiptNumber}`))
  lines.push('')
  
  // Column widths so headers and values line up vertically
  // Column widths tuned so "Item Price Total" always fits on one row
  // 16 (item+qty) + 8 (price) + 8 (total) = 32 chars, under the 40-char limit
  const ITEM_COL = 16
  const PRICE_COL = 8
  const TOTAL_COL = 8

  // Items header - now only shows Item, Price, Total (quantity is shown as *Q on the item)
  lines.push(clampLine(
    padRight('Item', ITEM_COL) +
    padLeft('Price', PRICE_COL) +
    padLeft('Total', TOTAL_COL)
  ))
  lines.push(lineSeparator)
  lines.push('')
  
  // Items - quantity is displayed inline on the name as "*Q"
  
  cartItems.forEach(item => {
    const fullName = (item.name || 'Item')
    const qty = String(item.quantity || 1)
    const price = currency(item.price || 0)
    const itemTotal = currency((item.price || 0) * (item.quantity || 1))

    // Show quantity inside the name column as "*Q"
    const nameWithQty = `${fullName} *${qty}`

    // Reserve fixed width for numeric columns (Price + Total)
    const numericWidth = PRICE_COL + TOTAL_COL
    const maxNameLen = Math.max(1, RECEIPT_WIDTH - numericWidth)
    const firstLineName = nameWithQty.substring(0, maxNameLen)

    // Main line: name+qty column + right-aligned numeric columns
    lines.push(clampLine(
      padRight(firstLineName, ITEM_COL) +
      padLeft(price, PRICE_COL) +
      padLeft(itemTotal, TOTAL_COL)
    ))

    // Any remaining part of the name (with qty) can overflow on its own lines (no price/total),
    // which keeps numeric columns on a single row.
    let overflow = nameWithQty.substring(firstLineName.length)
    while (overflow.length > 0) {
      lines.push(overflow.substring(0, RECEIPT_WIDTH))
      overflow = overflow.substring(RECEIPT_WIDTH)
    }
  })
  
  lines.push('')
  lines.push(lineSeparator)
  lines.push('')
  
  // Determine currency symbol
  const currencySymbol = isZigPayment ? 'ZIG' : 'USD'
  
  // Totals - compact label + value on a single line, with extra spacing between rows
  lines.push(clampLine(`Subtotal: ${currencySymbol} ${currency(subtotal)}`))
  lines.push('')
  
  // Discount (if applicable)
  if (discount > 0 && discountName) {
    const discountLabel = `Discount (${discountName}):`
    lines.push(clampLine(`${discountLabel} -${currencySymbol} ${currency(discount)}`))
    lines.push('')
  }
  
  // Total
  const totalLabel = isZigPayment ? 'Total (ZIG):' : 'Total:'
  lines.push(clampLine(`${totalLabel} ${currencySymbol} ${currency(total)}`))
  lines.push('')
  
  // Payment info (if needed) - only show if there's payment info
  if (amountReceived > 0) {
    const paymentMethodDisplay = paymentMethodFormatted || paymentMethod.toUpperCase()
    lines.push(clampLine(`Payment Method: ${paymentMethodDisplay}`))
    lines.push('')
    lines.push(clampLine(`Amount Received: ${currencySymbol} ${currency(amountReceived)}`))
    lines.push('')
  }
  if (changeDue > 0) {
    lines.push(clampLine(`Change: ${currencySymbol} ${currency(changeDue)}`))
    lines.push('')
  }
  
  lines.push('')
  lines.push(lineSeparator)
  lines.push('')
  
  // Footer - keep well under printer width so it never wraps
  lines.push(clampLine('Transforming spaces, Enhancing Lives,'))
  lines.push('')
  lines.push(separator)

  return lines.join('\n')
}

/**
 * Converts computed styles to inline style string
 * @param {HTMLElement} element - The element to get styles from
 * @returns {string} - Inline style string
 */
const getInlineStyles = (element) => {
  const computed = window.getComputedStyle(element)
  const styles = []
  
  // Get all relevant style properties for receipt printing
  const styleProps = [
    // Layout
    'display', 'position', 'width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight',
    // Spacing
    'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    // Borders
    'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
    'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
    // Colors
    'backgroundColor', 'color',
    // Typography
    'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight',
    'textAlign', 'textDecoration', 'textTransform', 'letterSpacing',
    // Flexbox
    'flexDirection', 'justifyContent', 'alignItems', 'alignSelf', 'flex', 'flexWrap', 'gap',
    // Other
    'overflow', 'boxSizing', 'visibility', 'opacity', 'whiteSpace'
  ]
  
  for (const prop of styleProps) {
    const value = computed.getPropertyValue(prop)
    if (value && value !== 'none' && value !== 'normal' && value !== 'auto' && value !== '0px') {
      // Convert camelCase to kebab-case
      const kebabProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase()
      styles.push(`${kebabProp}: ${value}`)
    }
  }
  
  // Preserve existing inline styles (they take precedence)
  const existingInlineStyles = element.getAttribute('style') || ''
  if (existingInlineStyles) {
    // Combine: existing inline styles first, then computed styles
    return existingInlineStyles + (styles.length > 0 ? '; ' + styles.join('; ') : '')
  }
  
  return styles.join('; ')
}

/**
 * Converts HTML element to HTML string with inline styles
 * @param {HTMLElement} element - The DOM element to convert
 * @returns {string} - Complete HTML document with inline styles
 */
const elementToHTML = (element) => {
  if (!element) {
    throw new Error('Element is required')
  }

  if (!element.innerHTML || element.innerHTML.trim().length === 0) {
    throw new Error('Element has no content to render')
  }

  console.log('[PRINT] Converting element to HTML with inline styles...')
  console.log('[PRINT] Element:', element)
  console.log('[PRINT] Element innerHTML length:', element.innerHTML.length)

  // Clone the element to avoid modifying the original
  const clone = element.cloneNode(true)
  
  // Function to recursively apply inline styles to all elements
  const applyInlineStyles = (el) => {
    // Apply styles to current element
    const inlineStyles = getInlineStyles(el)
    if (inlineStyles) {
      el.setAttribute('style', inlineStyles)
    }
    
    // Recursively process children
    Array.from(el.children).forEach(child => {
      applyInlineStyles(child)
    })
  }
  
  // Apply inline styles to cloned element and all children
  applyInlineStyles(clone)
  
  // Get the HTML content
  const htmlContent = clone.outerHTML
  
  // Fix self-closing tags to be XML-compliant
  // Java XML parser (TrAX transformer) requires all tags to be properly closed
  // Convert HTML5 self-closing tags to XML format: <tag /> instead of <tag>
  let fixedHTML = htmlContent
    // Fix self-closing meta tags (must be closed with />)
    .replace(/<meta([^>]*?)(?:\s*\/)?>/gi, '<meta$1 />')
    // Fix self-closing input tags
    .replace(/<input([^>]*?)(?:\s*\/)?>/gi, '<input$1 />')
    // Fix self-closing img tags
    .replace(/<img([^>]*?)(?:\s*\/)?>/gi, '<img$1 />')
    // Fix self-closing br tags
    .replace(/<br(?:\s+[^>]*?)?(?:\s*\/)?>/gi, '<br />')
    // Fix self-closing hr tags
    .replace(/<hr(?:\s+[^>]*?)?(?:\s*\/)?>/gi, '<hr />')
    // Fix self-closing col tags
    .replace(/<col([^>]*?)(?:\s*\/)?>/gi, '<col$1 />')
    // Fix self-closing link tags
    .replace(/<link([^>]*?)(?:\s*\/)?>/gi, '<link$1 />')
    // Fix self-closing area tags
    .replace(/<area([^>]*?)(?:\s*\/)?>/gi, '<area$1 />')
    // Fix self-closing base tags
    .replace(/<base([^>]*?)(?:\s*\/)?>/gi, '<base$1 />')
    // Fix self-closing source tags
    .replace(/<source([^>]*?)(?:\s*\/)?>/gi, '<source$1 />')
    // Fix self-closing track tags
    .replace(/<track([^>]*?)(?:\s*\/)?>/gi, '<track$1 />')
    // Fix self-closing wbr tags
    .replace(/<wbr(?:\s+[^>]*?)?(?:\s*\/)?>/gi, '<wbr />')

  // Wrap in a complete HTML document with XML-compliant tags
  const fullHTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Receipt</title>
</head>
<body style="margin: 0; padding: 0; background: #ffffff;">
  ${fixedHTML}
</body>
</html>`

  console.log('[PRINT] HTML generated, length:', fullHTML.length)
  return fullHTML
}

/**
 * Sends plain text receipt to Local Print Service
 * @param {string} plainText - Plain text receipt content
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
const sendPlainTextToPrintService = async (plainText) => {
  try {
    console.log('[PRINT] Sending plain text receipt to print service...')
    console.log('[PRINT] Content-Type: text/plain; charset=utf-8')
    console.log('[PRINT] Text length:', plainText.length, 'characters')
    console.log('[PRINT] Receipt preview:')
    console.log(plainText.substring(0, 200))

    const response = await fetch(PRINT_SERVICE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain; charset=utf-8',
      },
      body: plainText, // Send plain text string directly
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || `HTTP ${response.status}`)
    }

    const result = await response.text()
    console.log('[PRINT] ✅ Plain text receipt printed successfully')
    return { success: true, message: result }
  } catch (error) {
    console.error('[PRINT] ❌ Plain text print error:', error.message)
    
    // Check if it's a network/connection error
    const isConnectionError = 
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('ERR_CONNECTION_REFUSED') ||
      error.message.includes('ERR_INTERNET_DISCONNECTED')

    if (isConnectionError) {
      throw new Error('PRINT_SERVICE_UNAVAILABLE')
    }

    // Re-throw other errors
    throw error
  }
}

/**
 * Prints receipt via Local Print Service
 * Formats receipt data as plain text and sends to print service
 * Uses Plain Text printing for best compatibility with receipt printers
 * @param {Object} receiptData - Receipt data object with all receipt information
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const printViaLocalService = async (receiptData) => {
  if (!receiptData) {
    throw new Error('Receipt data is required for printing')
  }

  try {
    // Step 1: Format receipt data as plain text
    console.log('[PRINT] Formatting receipt data as plain text...')
    const plainText = formatReceiptAsPlainText(receiptData)
    
    // Step 2: Send plain text to print service
    console.log('[PRINT] Sending plain text receipt to print service...')
    const result = await sendPlainTextToPrintService(plainText)
    
    console.log('[PRINT] ✅ Successfully printed via plain text')
    return result
  } catch (error) {
    // Check if it's a network/connection error
    const isConnectionError = 
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('ERR_CONNECTION_REFUSED') ||
      error.message.includes('ERR_INTERNET_DISCONNECTED')

    if (isConnectionError) {
      throw new Error('PRINT_SERVICE_UNAVAILABLE')
    }

    // Re-throw other errors
    throw error
  }
}

/**
 * Prints receipt as ESC/POS commands (raw bytes) via Local Print Service
 * @param {Object} receiptData - Receipt data object
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
const printViaLocalServiceEscPos = async (receiptData) => {
  try {
    const escPosData = formatReceiptAsEscPos(receiptData)

    // Verify it's a Uint8Array (raw bytes), not a string
    if (!(escPosData instanceof Uint8Array)) {
      throw new Error('ESC/POS data must be Uint8Array, got: ' + typeof escPosData)
    }

    // Ensure data is sent as raw bytes (ArrayBuffer)
    // Uint8Array.buffer gives us the underlying ArrayBuffer
    const arrayBuffer = escPosData.buffer.slice(
      escPosData.byteOffset,
      escPosData.byteOffset + escPosData.byteLength
    )

    console.log('[PRINT] Sending RAW ESC/POS commands...')
    console.log('[PRINT] Content-Type: application/octet-stream')
    console.log('[PRINT] Data type: ArrayBuffer')
    console.log('[PRINT] Data length:', arrayBuffer.byteLength, 'bytes')
    console.log('[PRINT] First byte:', escPosData[0], '(should be 0x1B = ESC)')
    console.log('[PRINT] Second byte:', escPosData[1], '(should be 0x40 = @)')

    const response = await fetch(PRINT_SERVICE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      body: arrayBuffer, // Raw ArrayBuffer bytes
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || `HTTP ${response.status}`)
    }

    const result = await response.text()
    return { success: true, message: result }
  } catch (error) {
    // Check if it's a network/connection error
    const isConnectionError = 
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('ERR_CONNECTION_REFUSED') ||
      error.message.includes('ERR_INTERNET_DISCONNECTED')

    if (isConnectionError) {
      throw new Error('PRINT_SERVICE_UNAVAILABLE')
    }

    // Re-throw other errors
    throw error
  }
}

/**
 * Checks if the Local Print Service is available
 * @returns {Promise<boolean>}
 */
export const checkPrintServiceAvailable = async () => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 1000)

    // Try to connect (OPTIONS might work, or we can try a simple POST with empty body)
    const response = await fetch(PRINT_SERVICE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain; charset=utf-8',
      },
      body: '',
      signal: controller.signal,
    })

    clearTimeout(timeoutId)
    return response.status !== 404 // Service exists if not 404
  } catch (error) {
    return false
  }
}

